import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Spin } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import { AssignedRulesContextMenu } from 'components/atoms/AssignedRulesContextMenu';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { SyncProgressBanner } from 'components/atoms/SyncProgressBanner';
import WimsicalError from 'components/atoms/WimsicalError/WimsicalError';
import { AssignedRulesEditModal } from 'components/molecules/AssignedRulesEditModal';
import { AssignedRulesControls } from 'components/organisms/AssignedRulesControls';
import { RulesTable } from 'components/organisms/RulesTable';
import { useCatalogSync } from 'hooks/useCatalogSync';
import { ProductCatalogItem } from 'models/ProductCatalog';
import { RenderedCell } from 'rc-table/lib/interface';
import React, { MouseEvent, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetProductCatalogItemsQuery } from 'redux/services/julia/juliaApi';
import { setPriceBookSourceProductNumber } from 'redux/services/julia/priceBookEntryParams';
import { setOffset, setTake } from 'redux/services/julia/productCatalogParams';
import { useCreateCatalogConfigMutation, useGetCatalogConfigQuery, useGetXrefConfigQuery } from 'redux/services/rudyCadabby/rudyCadabbyApi';
import { addSelectedRule, setSelectedRules } from 'redux/slices/assignedRulesSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

export const AssignedRulesPage = (): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { id: entityId } = useParams();

  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isBusinessEdit = pathname.includes('business/');

  const { juliaProductCatalogParams, assignedRules } = useAppSelector((state) => state);

  /* ****************** API Hooks ****************** */
  const {
    data: productCatalogItemsResponse,
    isLoading: isResponseLoading,
    isFetching: isResponseFetching,
    isError: isProductCatalogError,
    error: productCatalogError,
    refetch
  } = useGetProductCatalogItemsQuery({ customerId: entityId as string, params: juliaProductCatalogParams }, { skip: !entityId });

  const { data: catalogConfig, isLoading: isCatalogConfigLoading, isError: isCatalogConfigError } = useGetCatalogConfigQuery({ customerId: entityId as string }, { skip: !entityId });
  const [createBusinessCatalog, { isLoading: isCreateCatalogLoading }] = useCreateCatalogConfigMutation();
  const { data: xRefConfig } = useGetXrefConfigQuery({ customerId: entityId as string });

  // SignalR push notifications for catalog sync progress
  const handleSyncComplete = useCallback(() => refetch(), [refetch]);
  const { syncStatus, syncProgress } = useCatalogSync(isBusinessEdit ? undefined : entityId, handleSyncComplete);

  /* ******************** Variables / Functions ******************** */
  const isLoading = isResponseLoading;
  const isFetching = isResponseFetching;
  const data = productCatalogItemsResponse?.data;
  const totalCount = productCatalogItemsResponse?.totalCount || 0;
  const isNoInheritanceSelected = catalogConfig?.dataInheritanceLevel === undefined;
  const isBusinessOnly = catalogConfig?.dataInheritanceLevel === 'Business' && pathname.includes('/division/');
  const selectedKeys =
    assignedRules.selectedRules.length && productCatalogItemsResponse ? assignedRules.selectedRules.map((id) => productCatalogItemsResponse.data.find((item) => item.id === id)?.id ?? '') : [];

  const handleUpdateRecord = (productId: string): void => {
    navigate(`rules/${productId}`);
  };

  const handlePriceClick = (productNumber: string): void => {
    const prefix = isBusinessEdit ? 'business' : 'division';

    dispatch(setPriceBookSourceProductNumber(productNumber));
    navigate(`/${prefix}/${entityId}/price-book`);
  };

  const handleBusinessSelectRow = (selectedRowKeys: React.Key[], selectedRows: ProductCatalogItem[]): void => {
    dispatch(setSelectedRules(selectedRows.map((item) => item.id)));
  };

  const handleDivisionSelectRow = (selectedRowKeys: React.Key[], selectedRows: ProductCatalogItem[]): void => {
    // Handle deslect all
    if (selectedKeys.length === selectedRows.filter((item) => item.isOverridden).map((item) => item.id).length) {
      dispatch(setSelectedRules([]));

      return;
    }

    // Handle all the rest
    dispatch(setSelectedRules(selectedRows.filter((item) => item.isOverridden).map((item) => item.id)));
  };

  const handlePageChange = (page: number, pageSize: number): void => {
    dispatch(setTake(pageSize));
    dispatch(setOffset((page - 1) * pageSize));
  };

  const handleTableRightClick = (idNumber: string): void => {
    if (assignedRules.selectedRules.includes(idNumber) === false) dispatch(addSelectedRule(idNumber));
  };

  const checkedCellJSX = (checked: boolean, record: ProductCatalogItem, index: number, originalNode: React.ReactNode): React.ReactNode | RenderedCell<ProductCatalogItem> | undefined => {
    if (!record.isOverridden && !isBusinessEdit) return <></>;

    return originalNode;
  };

  const tableLoading = {
    spinning: isLoading || (isFetching && juliaProductCatalogParams.offset === 0),
    indicator: <BlockLoader direction="loader loader--slideUp" />
  };

  const rowSelectionConfig: TableRowSelection<ProductCatalogItem> = {
    type: 'checkbox',
    onChange: isBusinessEdit ? handleBusinessSelectRow : handleDivisionSelectRow,
    renderCell: checkedCellJSX,
    selectedRowKeys: selectedKeys
  };

  // Create catalog config if business doesnt have one
  useEffect(() => {
    if (isBusinessEdit && isCatalogConfigError && entityId) createBusinessCatalog({ customerId: entityId, payload: { dataInheritanceLevel: 'Business' } });
  }, [createBusinessCatalog, entityId, isBusinessEdit, isCatalogConfigError]);

  /* ****************** Render ****************** */
  if (isNoInheritanceSelected && !isCatalogConfigLoading) {
    navigate('/unauthorized');
  }

  if (isProductCatalogError) {
    const errData = productCatalogError as FetchBaseQueryError;

    return (
      <WimsicalError
        title={(errData.data as string) || 'Uh oh! The Product Catalog API is unreachable'}
        statusCode={errData.status}
        subTitle="Julia might be taking a nap. Check that the API is running and try again."
        redirectText="Try Again"
        redirectUrl={window.location.href}
      />
    );
  }

  return (
    <Spin spinning={!catalogConfig && isCatalogConfigLoading && isCatalogConfigError} indicator={<BlockLoader direction="loader loader--slideUp" />}>
      {syncStatus === 'syncing' && <SyncProgressBanner processed={syncProgress.processed} total={syncProgress.total} />}
      <AssignedRulesControls />
      <AssignedRulesContextMenu>
        <RulesTable
          xRefLabel={xRefConfig?.alternateItemIdLabel}
          onProductNumber={handleUpdateRecord}
          onPriceClick={handlePriceClick}
          loading={tableLoading}
          dataSource={data}
          scroll={{ x: 'max-content' }}
          rowSelection={!isBusinessOnly ? rowSelectionConfig : undefined}
          onRow={(record): { onContextMenu: (event: MouseEvent) => void } => ({
            onContextMenu: (event): void => {
              event.preventDefault();
              if (record.isOverridden || isBusinessEdit) handleTableRightClick(record.id);
            }
          })}
          size="small"
          rowKey={(item): string => item.id}
          pagination={{ total: totalCount, defaultPageSize: juliaProductCatalogParams.take, onChange: handlePageChange, pageSizeOptions: ['10', '20', '25', '50', '100'] }}
        />
      </AssignedRulesContextMenu>
      <AssignedRulesEditModal />
    </Spin>
  );
};
